import ExampleCheck from '@ar/assets/images/example-eft-check.svg';
import { zodResolver } from '@hookform/resolvers/zod';
import ArrowRightIcon from '@icons/arrow-right-icon.svg';
import CheckIcon from '@icons/check-icon.svg';
import { Button, Input, Separator, Typography } from '@notch-ordering/ui-components';
import React, { useState } from 'react';
import { Controller, useForm, useFormState } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { AccountRole, attachAdyenPaymentMethod, GetPaymentMethodsResponse, PaymentMethodType } from '@/ar/network/AccountsReceivable.network';
import { CreatePaymentMethodSteps } from '../CreateAdyenPaymentMethodConstants';
import { BankAccountDetails, CreateBankAccountProps, encryptBankAccountData, EncryptedBankData, NUM_FIELDS, validationSchemaACH, validationSchemaEFT } from './CreateBankAccountConstants';
import { MandateModal } from './MandateModal/MandateModal';
import { useSupplierStore } from '@/ar/stores/SupplierStore';
import { useCreatePaymentMethodStore } from '@/ar/stores/CreatePaymentMethodStore';
import { FETCH_BULK_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY } from '@/ar/components/Invoices/Modals/BulkManualChargeModal/BulkManualChargeModalConstants';
import { FETCH_BALANCES_QUERY_KEY } from '@/ar/hooks/queries/BalanceQueries.hook';
import { FETCH_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY } from '@/ar/hooks/queries/CustomerQueries.hook';
import { queryClient } from '@/containers/app/Root';

export const CreateBankAccount = ({ handleClose, customerID, supplierID }: CreateBankAccountProps): JSX.Element => {
    const [bankData, setBankData] = useState<EncryptedBankData | null>(null);
    const { currentStep, setCurrentStep, paymentMethodType, setMandateId } = useCreatePaymentMethodStore();
    const isACHPaymentMethod = paymentMethodType === PaymentMethodType.US_BANK_ACCOUNT;
    const isEFTPaymentMethod = paymentMethodType === PaymentMethodType.ACSS_DEBIT;
    const attachPaymentMethodMutation = useMutation(attachAdyenPaymentMethod);
    const { supplierLoginData } = useSupplierStore();
    const isCustomer = supplierLoginData?.isCustomer;

    const { handleSubmit, control, reset } = useForm<BankAccountDetails>({
        resolver: zodResolver(isACHPaymentMethod ? validationSchemaACH : validationSchemaEFT),
        defaultValues: {
            ownerName: '',
            branchNumber: '',
            institutionNumber: '',
            accountNumber: '',
            confirmAccountNumber: '',
            routingNumber: '',
        },
        mode: 'onBlur',
    });

    const { errors, touchedFields, isDirty } = useFormState({
        control,
    });

    const resetForm = (): void => {
        reset();
    };

    const handleOnClose = ():void => {
        resetForm();
        handleClose();
    };

    const handleCustomerFormSubmit = async (newFormBankAccount: BankAccountDetails): Promise<void> => {
        const encryptedBankInfo = await encryptBankAccountData(newFormBankAccount, isACHPaymentMethod);
        await attachPaymentMethodMutation.mutateAsync({
            body: {
                bankData: {
                    type: paymentMethodType,
                    accountRole: AccountRole.CHECKING,
                    ownerName: encryptedBankInfo?.encryptedOwnerName,
                    routingNumber: paymentMethodType === PaymentMethodType.US_BANK_ACCOUNT ? encryptedBankInfo?.encryptedRoutingNumber : undefined,
                    transitNumber: paymentMethodType === PaymentMethodType.ACSS_DEBIT ? encryptedBankInfo?.encryptedBranchNumber : undefined,
                    institutionNumber: paymentMethodType === PaymentMethodType.ACSS_DEBIT ? encryptedBankInfo?.encryptedInstitutionNumber : undefined,
                    accountNumber: encryptedBankInfo?.encryptedAccountNumber
                },
                customerId: customerID,
                supplierId: supplierID,
                // setting false for customers to avoid sending mandate email but still need to accept the mandate in the next step
                isMandateAttestedOffline: false,
                sendMandateEmail: false,
            }
        }, { onSuccess: async (data) => {
            await queryClient.invalidateQueries([FETCH_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY]);
            await queryClient.invalidateQueries([FETCH_BALANCES_QUERY_KEY]);
            await queryClient.invalidateQueries([FETCH_BULK_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY]);
            // after invalidate queries check the payment method id and get the mandate id from the payment methods
            const paymentMethods = queryClient.getQueryData<GetPaymentMethodsResponse>([FETCH_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY, customerID, supplierID]);
            const paymentMethod = paymentMethods?.payment_methods.find((pm) => pm.id === data.payment_method_id);
            const mandateId = paymentMethod?.mandate_id;
            setMandateId(mandateId);
        } });
        setCurrentStep(CreatePaymentMethodSteps.BANK_ACCOUNT_ACCEPT_MANDATE);
    };

    const onSubmit = async (newFormBankAccount: BankAccountDetails): Promise<void> => {
        if (!isCustomer) {
            // supplier flow we need to encrypt the form data and pass it to the mandate step where supplier will either send the mandate or accept the mandate offline
            setBankData(await encryptBankAccountData(newFormBankAccount, isACHPaymentMethod));
            setCurrentStep(CreatePaymentMethodSteps.BANK_ACCOUNT_MANDATE);
            resetForm();
        } else {
            // if a customer, we need to submit the form data and create the payment method as online and prevent sending the mandate email,
            // and TODO: then navigate to the next step to accept the mandate
            handleCustomerFormSubmit(newFormBankAccount);
        }
    };

    const hasErrors = Object.keys(errors).some((key) => errors[key]);
    const allFieldsTouched = Object.keys(touchedFields).length === NUM_FIELDS[paymentMethodType] && Object.values(touchedFields).every((value) => value);
    const isSubmitDisabled = hasErrors || !allFieldsTouched || !isDirty;
    if (!paymentMethodType) {
        return null;
    }

    return <> {currentStep === CreatePaymentMethodSteps.ADD_BANK_ACCOUNT
        && <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-8 py-6 min-w-0">
                <div className="flex-col flex gap-4">
                    <Typography weight="font-regular" variant="BASE" desktopSize="lg:text-3.5" className="m-0">Enter your customer&apos;s bank account information.</Typography>
                    <Controller
                        name="ownerName"
                        control={control}
                        render={({ field }) => <Input
                            name="ownerName"
                            id="ownerName"
                            label="Name of account holder"
                            type="TEXT"
                            inputProps={{
                                placeholder: 'Must match name on account',
                                ...field
                            }}
                            variant="MEDIUM"
                            inputClassName="h-9 text-3.5"
                            fontSize="[&>label]:font-medium [&>label]:lg:text-1 [&>label]:text-gray-600 [&>label]:m-0"
                            className="gap-1"
                            required
                            isInvalid={!!errors.ownerName}
                            invalidMessage={errors.ownerName?.message}
                            labelIcon={touchedFields.ownerName && !errors.ownerName ? <CheckIcon data-testid="owner-name-check" className="h-4 w-4 text-green-400" /> : null} />} />
                    {isACHPaymentMethod
                            && <Controller
                                name="routingNumber"
                                control={control}
                                render={({ field }) => <Input
                                    name="routingNumber"
                                    id="routingNumber"
                                    label="Routing number"
                                    type="NUMBER"
                                    inputProps={{
                                        placeholder: '9 digits',
                                        ...field
                                    }}
                                    variant="MEDIUM"
                                    inputClassName="h-9 text-3.5"
                                    fontSize="[&>label]:font-medium [&>label]:lg:text-1 [&>label]:text-gray-600 [&>label]:m-0"
                                    className="gap-1"
                                    required
                                    isInvalid={!!errors.routingNumber}
                                    invalidMessage={errors.routingNumber?.message}
                                    labelIcon={touchedFields.routingNumber && !errors.routingNumber ? <CheckIcon data-testid="routing-number-check" className="h-4 w-4 text-green-400" /> : null} />} />
                    }
                    {isEFTPaymentMethod && <>
                        <Controller
                            name="branchNumber"
                            control={control}
                            render={({ field }) => <Input
                                name="branchNumber"
                                id="branchNumber"
                                label="Branch number"
                                type="NUMBER"
                                inputProps={{
                                    placeholder: '5 digits',
                                    ...field
                                }}
                                variant="MEDIUM"
                                inputClassName="h-9 text-3.5"
                                fontSize="[&>label]:font-medium [&>label]:lg:text-1 [&>label]:text-gray-600 [&>label]:m-0"
                                className="gap-1"
                                required
                                isInvalid={!!errors.branchNumber}
                                invalidMessage={errors.branchNumber?.message}
                                labelIcon={touchedFields.branchNumber && !errors.branchNumber ? <CheckIcon data-testid="branch-number-check" className="h-4 w-4 text-green-400" /> : null} />} />
                        <Controller
                            name="institutionNumber"
                            control={control}
                            render={({ field }) => <Input
                                name="institutionNumber"
                                id="institutionNumber"
                                label="Institution number"
                                type="NUMBER"
                                inputProps={{
                                    placeholder: '3 digits',
                                    ...field
                                }}
                                variant="MEDIUM"
                                inputClassName="h-9 text-3.5"
                                fontSize="[&>label]:font-medium [&>label]:lg:text-1 [&>label]:text-gray-600 [&>label]:m-0"
                                className="gap-1"
                                required
                                isInvalid={!!errors.institutionNumber}
                                invalidMessage={errors.institutionNumber?.message}
                                labelIcon={touchedFields.institutionNumber && !errors.institutionNumber ? <CheckIcon data-testid="institution-number-check" className="h-4 w-4 text-green-400" /> : null} />} />
                    </>}
                    <Controller
                        name="accountNumber"
                        control={control}
                        render={({ field }) => <Input
                            name="accountNumber"
                            id="accountNumber"
                            label="Account number"
                            type="NUMBER"
                            inputProps={{
                                placeholder: '7-12 digits',
                                ...field
                            }}
                            variant="MEDIUM"
                            inputClassName="h-9 text-3.5"
                            fontSize="[&>label]:font-medium [&>label]:lg:text-1 [&>label]:text-gray-600 [&>label]:m-0"
                            className="gap-1"
                            required
                            isInvalid={!!errors.accountNumber}
                            invalidMessage={errors.accountNumber?.message}
                            labelIcon={touchedFields.accountNumber && !errors.accountNumber ? <CheckIcon data-testid="account-number-check" className="h-4 w-4 text-green-400" /> : null} />} />
                    <Controller
                        name="confirmAccountNumber"
                        control={control}
                        render={({ field }) => <Input
                            name="confirmAccountNumber"
                            id="confirmAccountNumber"
                            label="Confirm account number"
                            type="NUMBER"
                            inputProps={{
                                placeholder: '7-12 digits',
                                ...field
                            }}
                            variant="MEDIUM"
                            inputClassName="h-9 text-3.5"
                            fontSize="[&>label]:font-medium [&>label]:lg:text-1 [&>label]:text-gray-600 [&>label]:m-0"
                            className="gap-1"
                            required
                            isInvalid={!!errors.confirmAccountNumber}
                            invalidMessage={errors.confirmAccountNumber?.message}
                            labelIcon={touchedFields.confirmAccountNumber && !errors.confirmAccountNumber ? <CheckIcon data-testid="confirm-account-number-check" className="h-4 w-4 text-green-400" /> : null} />} />
                    <ExampleCheck/>
                </div>
            </div>

            <Separator />
            <div className="pt-4 px-6 pb-1 flex justify-end gap-3">
                <Button variant="TERTIARY_FILLED"
                    as={'span'}
                    className="cursor-pointer"
                    size="SMALL"
                    onClick={handleOnClose}>
                    <Typography as="span" weight="font-medium">
                            Cancel
                    </Typography>
                </Button>
                <Button variant="SECONDARY"
                    size="SMALL"
                    disabled={isSubmitDisabled}
                    loading={attachPaymentMethodMutation.isLoading}
                    type="submit">
                    <div className="flex gap-1">
                        <Typography as="span" weight="font-medium">
                            {isCustomer ? 'Save' : 'Save & next'}
                        </Typography>
                        <ArrowRightIcon className="w-4 h-4 mt-0.5" />
                    </div>
                </Button>
            </div>
        </form>}
    {(currentStep === CreatePaymentMethodSteps.BANK_ACCOUNT_MANDATE)
    && <MandateModal
        isModal={false}
        isOpen={(currentStep === CreatePaymentMethodSteps.BANK_ACCOUNT_MANDATE)}
        handleClose={() => {
            setCurrentStep(CreatePaymentMethodSteps.SELECT_PAYMENT_METHOD);
            handleClose();
        }}
        bankData={bankData}
        customerID={customerID}
        supplierID={supplierID}
        paymentMethodType={paymentMethodType}/>
    }
    </>;
};
